// @ts-ignore
import * as swcHelpers from '@swc/helpers';

/* eslint-disable no-eval */
// import buildProcess from "./utils/process";
import * as logger from '../../utils/logger';

// Declare that swcHelpers can exist on the global window object
declare global {
  interface Window {
    swcHelpers?: {
      interopRequireDefault?: Function;
      interopRequireWildcard?: Function;
      [key: string]: any;
    };
  }
}

const g = typeof window === 'undefined' ? self : window;

const hasGlobalDeclaration = /^const global/m;

// Debug helper function to log object structure
const debugObjectStructure = (obj: any, name: string = 'object') => {
  try {
    logger.debug(`${name} type: ${typeof obj}`);
    if (obj) {
      logger.debug(`${name} keys: ${Object.keys(obj).join(', ')}`);
      Object.keys(obj).forEach(key => {
        const value = obj[key];
        logger.debug(`${name}.${key} type: ${typeof value}`);
        if (typeof value === 'object' && value) {
          logger.debug(`${name}.${key} has keys: ${Object.keys(value).join(', ')}`);
        }
      });
    } else {
      logger.debug(`${name} is null or undefined`);
    }
  } catch (e) {
    logger.debug(`Error examining ${name}: ${e}`);
  }
};

// Patch the global window object with our SWC helpers before any code runs
const patchGlobalWithSWCHelpers = () => {
  // Create a global swcHelpers object if it doesn't exist
  if (!g.swcHelpers) {
    g.swcHelpers = {};
  }
  
  // Define our reliable implementations
  const interopRequireDefault = (obj: any) => {
    return obj && obj.__esModule ? obj : { default: obj };
  };
  
  const interopRequireWildcard = (obj: any) => {
    if (!obj || obj.__esModule) {
      return obj;
    }
    
    const newObj: any = {};
    if (obj != null) {
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          newObj[key] = obj[key];
        }
      }
    }
    
    newObj.default = obj;
    return newObj;
  };
  
  // Add these helpers to the global swcHelpers object
  // Only add if they don't already exist or are not functions
  if (typeof g.swcHelpers.interopRequireDefault !== 'function') {
    g.swcHelpers.interopRequireDefault = interopRequireDefault;
    logger.debug('Added interopRequireDefault to global swcHelpers');
  }
  
  if (typeof g.swcHelpers.interopRequireWildcard !== 'function') {
    g.swcHelpers.interopRequireWildcard = interopRequireWildcard;
    logger.debug('Added interopRequireWildcard to global swcHelpers');
  }
  
  // You can add other helpers here as needed
  
  return g.swcHelpers;
};

// Initialize the global swcHelpers immediately
const globalSwcHelpers = patchGlobalWithSWCHelpers();
logger.debug('Global swcHelpers patched with methods:', Object.keys(globalSwcHelpers).join(', '));

/* eslint-disable no-unused-vars */
export default function (
  code: string,
  require: Function,
  context: { id: string; exports: any; hot?: any },
  env: Object = {},
  globals: Object = {}
) {
  const global = g;
  const process = {
    env: {
      NODE_ENV: 'development',
    },
  }; // buildProcess(env);
  // @ts-ignore
  g.global = global;
  
  // Make sure swcHelpers are patched again before each evaluation
  // This ensures g.swcHelpers.interopRequireDefault and g.swcHelpers.interopRequireWildcard
  // (camelCase keys) point to our custom implementations.
  patchGlobalWithSWCHelpers();
  
  const allGlobals: { [key: string]: any } = {
    require,
    module: context,
    exports: context.exports,
    process,
    global,

    // Spread all standard SWC helpers from the import '@swc/helpers'.
    // These are snake_case (e.g., _interop_require_wildcard, _async_to_generator).
    ...swcHelpers,

    // Override _interop_require_default and _interop_require_wildcard
    // with our custom implementations. These custom functions are stored
    // by patchGlobalWithSWCHelpers on g.swcHelpers (using camelCase keys).
    _interop_require_default: g.swcHelpers!.interopRequireDefault,
    _interop_require_wildcard: g.swcHelpers!.interopRequireWildcard,
    
    // Spread any additional user-provided globals
    ...globals,
  };

  if (hasGlobalDeclaration.test(code)) {
    delete allGlobals.global;
  }

  const allGlobalKeys = Object.keys(allGlobals);
  const globalsCode = allGlobalKeys.length ? allGlobalKeys.join(', ') : '';
  const globalsValues = allGlobalKeys.map((k) => allGlobals[k]);
  try {
    const newCode = `(function $orca$eval(` + globalsCode + `) {` + code + `\n})`;
    // @ts-ignore
    (0, eval)(newCode).apply(allGlobals.global, globalsValues);

    return context.exports;
  } catch (err) {
    logger.error(err);
    logger.error(code);

    let error = err;
    if (typeof err === 'string') {
      error = new Error(err);
    }
    // @ts-ignore
    error.isEvalError = true;

    throw error;
  }
}
